// The SMS message editor for chat approval and message-review cards. It
// wraps the shared template editor so caregiver variables render as an
// inline pill inside the box (draggable into and within the editor) instead
// of literal `{{...}}` braces. The canonical in-card string keeps the token
// in single braces; callers decide at submit time
// whether to expand it per recipient or hand the token back to the agent.
import { type ReactNode, type Ref } from 'react';

import {
    type TemplateVariable,
    VariableTextEditor,
    type VariableTextEditorActions,
} from '@apps/web/components/shared/messages/variable_text_editor';
import { type CaregiverTemplateValues } from '@apps/web/features/phoebe_chat_v3/registry/types';

export const CAREGIVER_NAME_TOKEN = '{caregiver_name}';
export const CAREGIVER_FIRST_NAME_TOKEN = '{caregiver_first_name}';
export const CAREGIVER_LAST_NAME_TOKEN = '{caregiver_last_name}';
export const FIRST_NAME_TOKEN = CAREGIVER_FIRST_NAME_TOKEN;

export const SMS_MESSAGE_VARIABLES: ReadonlyArray<TemplateVariable> = [
    { token: 'caregiver_first_name', label: 'First name' },
    { token: 'caregiver_last_name', label: 'Last name' },
    { token: 'caregiver_name', label: 'Full name' },
];

function recipientNameParts(recipientName: string): Readonly<{
    readonly firstName: string;
    readonly lastName: string;
}> {
    const [firstName = '', ...remaining] = recipientName.trim().split(/\s+/);
    return { firstName, lastName: remaining.join(' ') };
}

export function caregiverTemplateValuesFromName(
    recipientName: string,
): CaregiverTemplateValues {
    const caregiverName = recipientName.trim().replace(/\s+/g, ' ');
    const { firstName, lastName } = recipientNameParts(caregiverName);
    return {
        caregiver_first_name: firstName,
        caregiver_last_name: lastName,
        caregiver_name: caregiverName,
    };
}

export function prependFirstNameToken(message: string): string {
    const needsSeparator = message !== '' && !/^[\s,.;:!?]/.test(message);
    return `${FIRST_NAME_TOKEN}${needsSeparator ? ' ' : ''}${message}`;
}

/** Rewrites the legacy `{{first_name}}` spelling to the editor's token. */
export function normalizeFirstNameToken(message: string): string {
    return normalizeCaregiverTemplateTokens(message);
}

export function normalizeCaregiverTemplateTokens(message: string): string {
    return [
        ['first_name', 'caregiver_first_name'],
        ['caregiver_first_name', 'caregiver_first_name'],
        ['caregiver_last_name', 'caregiver_last_name'],
        ['caregiver_name', 'caregiver_name'],
    ].reduce(
        (current, [legacy, canonical]) =>
            current
                .split(`{{${legacy}}}`)
                .join(`{${canonical}}`)
                .split(`{${legacy}}`)
                .join(`{${canonical}}`),
        message,
    );
}

function escapeRegularExpression(value: string): string {
    return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function replaceStandaloneName(
    message: string,
    name: string,
    token: string,
): string {
    if (name === '') {
        return message;
    }
    const pattern = new RegExp(
        `(^|[^\\p{L}\\p{M}\\p{N}_])${escapeRegularExpression(name)}(?=$|[^\\p{L}\\p{M}\\p{N}_])`,
        'giu',
    );
    return message.replace(pattern, (_match, prefix: string) => prefix + token);
}

function replaceGreetingName(
    message: string,
    name: string,
    token: string,
): string {
    if (name === '') {
        return message;
    }
    const pattern = new RegExp(
        `(^|\\b(?:hi|hey|hello)[, ]+)${escapeRegularExpression(name)}(?=$|[^\\p{L}\\p{M}\\p{N}_])`,
        'giu',
    );
    return message.replace(pattern, (_match, prefix: string) => prefix + token);
}

/**
 * Keeps staged personalized content editable as reusable variables. Names
 * must be standalone, so a recipient named Ann does not alter “Annabelle”.
 */
export function normalizeFirstNameTokenForRecipient(
    message: string,
    recipientName: string,
): string {
    return normalizeCaregiverTemplateTokensForRecipient(message, recipientName);
}

/** Turns staged literal caregiver names into per-recipient variables. */
export function normalizeCaregiverTemplateTokensForRecipient(
    message: string,
    recipient: string | CaregiverTemplateValues,
): string {
    const normalizedMessage = normalizeCaregiverTemplateTokens(message);
    const values =
        typeof recipient === 'string'
            ? caregiverTemplateValuesFromName(recipient)
            : recipient;
    const withFullName = replaceStandaloneName(
        normalizedMessage,
        values.caregiver_name,
        CAREGIVER_NAME_TOKEN,
    );
    return replaceGreetingName(
        withFullName,
        values.caregiver_first_name,
        CAREGIVER_FIRST_NAME_TOKEN,
    );
}

/** Restores the agent-facing first-name spelling for legacy wire payloads. */
export function denormalizeFirstNameToken(message: string): string {
    return SMS_MESSAGE_VARIABLES.reduce(
        (current, variable) =>
            current.split(`{${variable.token}}`).join(`{{${variable.token}}}`),
        normalizeCaregiverTemplateTokens(message),
    );
}

/**
 * Substitutes caregiver values for supported canonical and legacy tokens.
 */
export function expandFirstNameToken(
    message: string,
    recipientName: string,
): string {
    return expandCaregiverTemplateTokens(message, recipientName);
}

export function expandCaregiverTemplateTokens(
    message: string,
    recipient: string | CaregiverTemplateValues,
): string {
    const values =
        typeof recipient === 'string'
            ? caregiverTemplateValuesFromName(recipient)
            : recipient;
    return normalizeCaregiverTemplateTokens(message)
        .split(CAREGIVER_FIRST_NAME_TOKEN)
        .join(values.caregiver_first_name)
        .split(CAREGIVER_LAST_NAME_TOKEN)
        .join(values.caregiver_last_name)
        .split(CAREGIVER_NAME_TOKEN)
        .join(values.caregiver_name);
}

export type SmsMessageEditorProps = Readonly<{
    readonly value: string;
    readonly ariaLabel: string;
    readonly onChange?: (value: string) => void;
    readonly describedBy?: string;
    readonly actionRef?: Ref<VariableTextEditorActions>;
    readonly variables?: ReadonlyArray<TemplateVariable>;
    readonly removePillsOnDragOut?: boolean;
    readonly snapVariableDropsToWords?: boolean;
}>;

export function SmsMessageEditor({
    value,
    ariaLabel,
    onChange,
    describedBy,
    actionRef,
    variables = SMS_MESSAGE_VARIABLES,
    removePillsOnDragOut = false,
    snapVariableDropsToWords = false,
}: SmsMessageEditorProps): ReactNode {
    return (
        <VariableTextEditor
            value={value}
            onChange={onChange ?? (() => undefined)}
            readOnly={onChange === undefined}
            ariaLabel={ariaLabel}
            ariaDescribedby={describedBy}
            variables={variables}
            actionRef={actionRef}
            draggablePills={onChange !== undefined}
            removePillsOnDragOut={removePillsOnDragOut}
            snapVariableDropsToWords={snapVariableDropsToWords}
            // The shared editor's pills are sized for the settings pages;
            // scale them down to the chat's meta size so they sit inline
            // with the message text instead of inflating the line.
            className="[&_[data-variable-drop-preview]]:text-v3-chat-meta [&_[data-variable]]:text-v3-chat-meta [&_[data-variable]]:px-[5px]"
            editorClassName="bg-card dark:bg-card min-h-[calc(2lh+20px)] px-2.5 py-[9px] text-v3-chat shadow-none focus-visible:ring-0 focus-visible:border-agent-action md:text-v3-chat"
        />
    );
}
